import { useEffect, useRef, useCallback } from 'react';
import { API_CONFIG } from '../config';
import { apiFetch } from '../utils/api';
import { isSuperAdmin } from '../types';
import { useTranslation } from './useTranslation';
import type { AddNotificationOptions } from './useNotifications';
import type { NotificationType } from '../components/ui/Notification';

interface PendingBiddingRevision {
  id: string;
  pendingSheetId: string;
  sourceSheetId: string;
  editorName: string;
  summary: {
    projectName?: string;
    sheetName?: string;
    lastActivityAt?: string;
    editCount?: number;
  };
  status: string;
  updatedAt: string;
}

interface Args {
  userRole: string | null | undefined;
  addNotification: (
    type: NotificationType,
    title: string,
    message?: string,
    durationOrOptions?: number | AddNotificationOptions
  ) => string;
  upsertByPersistentId: (
    persistentId: string,
    type: NotificationType,
    title: string,
    message?: string,
    options?: AddNotificationOptions
  ) => void;
  removeByPersistentId: (persistentId: string) => void;
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message?: string) => void;
}

export function usePendingBiddingRevisions({
  userRole,
  addNotification,
  upsertByPersistentId,
  removeByPersistentId,
  showSuccess,
  showError,
}: Args): void {
  const { t } = useTranslation();
  const loadedRef = useRef<Set<string>>(new Set());
  const handlersRef = useRef({
    addNotification,
    upsertByPersistentId,
    removeByPersistentId,
    showSuccess,
    showError,
    t,
  });
  handlersRef.current = {
    addNotification,
    upsertByPersistentId,
    removeByPersistentId,
    showSuccess,
    showError,
    t,
  };

  const formatMessage = useCallback((req: PendingBiddingRevision, tr: typeof t) => {
    const s = req.summary || {};
    return tr('biddingRevision.toastMessage', {
      editor: req.editorName || '—',
      sheet: s.sheetName || req.pendingSheetId,
      project: s.projectName || '—',
      edits: String(s.editCount ?? 0),
    });
  }, []);

  const resolveRequest = useCallback(async (id: string, action: 'approve' | 'reject') => {
    const { removeByPersistentId: remove, showSuccess: ok, showError: fail, t: tr } =
      handlersRef.current;
    try {
      const response = await apiFetch(
        `${API_CONFIG.API_URL}/bidding/revisions/${id}/${action}`,
        { method: 'POST' }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `Failed to ${action}`);
      remove(id);
      loadedRef.current.delete(id);
      if (action === 'approve') {
        ok(tr('biddingRevision.approvedTitle'), tr('biddingRevision.approvedMessage'));
      } else {
        ok(tr('biddingRevision.rejectedTitle'), tr('biddingRevision.rejectedMessage'));
      }
    } catch (err) {
      fail(tr('biddingRevision.actionFailed'), err instanceof Error ? err.message : 'Error');
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin(userRole || null)) {
      for (const id of Array.from(loadedRef.current)) {
        removeByPersistentId(id);
      }
      loadedRef.current.clear();
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const response = await apiFetch(`${API_CONFIG.API_URL}/bidding/revisions/pending`);
        if (!response.ok) {
          console.error(`Failed to load bidding revisions: HTTP ${response.status}`);
          return;
        }
        const data = await response.json();
        const requests: PendingBiddingRevision[] = data.requests || [];
        if (cancelled) return;

        const { upsertByPersistentId: upsert, addNotification: add, t: tr } = handlersRef.current;
        const seen = new Set(requests.map((r) => r.id));

        for (const id of Array.from(loadedRef.current)) {
          if (!seen.has(id)) {
            handlersRef.current.removeByPersistentId(id);
            loadedRef.current.delete(id);
          }
        }

        for (const req of requests) {
          const title = tr('biddingRevision.toastTitle');
          const message = formatMessage(req, tr);
          const options: AddNotificationOptions = {
            duration: 0,
            persistentId: req.id,
            actions: [
              {
                label: tr('biddingRevision.approve'),
                variant: 'primary',
                onClick: () => resolveRequest(req.id, 'approve'),
              },
              {
                label: tr('biddingRevision.reject'),
                variant: 'danger',
                onClick: () => resolveRequest(req.id, 'reject'),
              },
            ],
          };

          if (loadedRef.current.has(req.id)) {
            upsert(req.id, 'warning', title, message, options);
          } else {
            loadedRef.current.add(req.id);
            add('warning', title, message, options);
          }
        }
      } catch (err) {
        console.error('Failed to load pending bidding revisions:', err);
      }
    };

    void load();
    const interval = setInterval(() => void load(), 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [userRole, resolveRequest, removeByPersistentId, formatMessage]);
}
